import { enUS, fi, fr, nb, sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export const LANGUAGES = [
  {
    code: "en",
    locale: "en-US",
    labelKey: "common.languageSelector.options.english",
    dayjsLocale: "en",
    dateFnsLocale: enUS,
  },
  {
    code: "sv",
    locale: "sv-SE",
    labelKey: "common.languageSelector.options.swedish",
    dayjsLocale: "sv",
    dateFnsLocale: sv,
  },
  {
    code: "fi",
    locale: "fi-FI",
    labelKey: "common.languageSelector.options.finnish",
    dayjsLocale: "fi",
    dateFnsLocale: fi,
  },
  {
    code: "no",
    locale: "no-NO",
    labelKey: "common.languageSelector.options.norwegian",
    // The translations live in public/locales/no, but dayjs and date-fns
    // name Bokmål "nb".
    dayjsLocale: "nb",
    dateFnsLocale: nb,
  },
  {
    code: "fr",
    locale: "fr-FR",
    labelKey: "common.languageSelector.options.french",
    dayjsLocale: "fr",
    dateFnsLocale: fr,
  },
] as const;

export type Language = (typeof LANGUAGES)[number];

const DEFAULT_LANGUAGE = LANGUAGES[0]; // English

// Browsers report Norwegian as nb/nn/no with or without a region; there is one
// Norwegian bundle. Reducing to the primary subtag is also what keeps a stale
// cached "sv-SE" Swedish once supportedLngs is set (i18next's first pass
// matches exact codes across all candidates).
export function toLanguageCode(tag: string): string {
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return ["nb", "nn", "no"].includes(primary) ? "no" : primary;
}

// After this change i18n.language and the cached value are always a supported
// code, so the English fallback only applies to an empty or stale storage read.
// Changing it is out of scope.
export const getCurrentLanguage = (i18nLanguage: string): Language =>
  LANGUAGES.find((lang) => lang.code === toLanguageCode(i18nLanguage)) ??
  DEFAULT_LANGUAGE;

// Shared by src/i18n.ts and the resolution tests, so they cannot drift.
export const LANGUAGE_RESOLUTION = {
  fallbackLng: "sv",
  supportedLngs: LANGUAGES.map((lang) => lang.code),
  detection: { convertDetectedLanguage: toLanguageCode },
};

export const svLocale = () =>
  localStorage.getItem("i18nextLng")?.startsWith("sv");
export const fiLocale = () =>
  localStorage.getItem("i18nextLng")?.startsWith("fi");
export const enLocale = () =>
  localStorage.getItem("i18nextLng")?.startsWith("en");
export const frLocale = () =>
  localStorage.getItem("i18nextLng")?.startsWith("fr");

export const fetchLang = () =>
  getCurrentLanguage(localStorage.getItem("i18nextLng") ?? "en").locale;

export const useCurrentLanguage = () => {
  const { i18n } = useTranslation();
  return getCurrentLanguage(i18n.language);
};

export const useFetchLang = () => useCurrentLanguage().locale;

export const dateFnsLocale = () =>
  getCurrentLanguage(localStorage.getItem("i18nextLng") ?? "en").dateFnsLocale;
